import React, { useContext } from "react";
import { mediaOutletUrl } from "views_shared/app/javascript/utils/urls";
import AboutSection from "../../profiles/sections/AboutSection";
import EmploymentSection from "../../profiles/sections/EmploymentSection";
import HashtagsCollapsible from "../../hashtags/HashtagsCollapsible";
import MediaRequestsSection from "./media_requests_section";
import MediaRequestsTeaser from "./media_requests_teaser";
import { MediaProfileContext } from "../data/media_profile_context_management";

function buildHashtagUrl(hashtag) {
  return `/media_profiles?hashtag=${encodeURIComponent(hashtag.hashtagName)}`;
}

const toEmploymentRow = (job) => ({
  id: job.id,
  href: mediaOutletUrl({ slug: job.mediaOutletSlug, migrate_id: job.mediaOutletMigrateId }),
  imageUrl: job.mediaOutletProfileImageUrl,
  name: job.mediaOutletName,
  jobTitle: job.title,
  from: job.from,
  to: job.to,
});

// Media Profile content as a stack of shared <SectionCard>s: About (bio, with an inside eyebrow row of
// the person's "Covers:" hashtags) and Employment (media jobs), read by the provider, plus Posts (the
// profile's visible requests, which the card pages through itself). About and Employment are core sections with no
// per-section show/hide config: an editable viewer always sees the card (with muted empty-state copy
// when there's nothing to show yet), while a non-editable viewer only sees the card once it actually
// has content, so a sparse profile with no bio, no chips, and no jobs shows no empty shells to a
// visitor. Requests is a paid, server-gated feature: entitled viewers get the real card (which itself
// renders only when the entitlement-scoped RPC returns rows); unentitled PR and expert viewers get an
// upsell teaser in its place; anyone else (anonymous, or another reporter, who can't buy a plan) gets
// neither. The remaining designed journalist-facing
// surfaces with no V2 data yet (clips, On-Qwoted stats) are deliberately absent until real data exists
// behind them. Edit affordances stay scattered (mirroring the PR page): About (bio, edited via the
// HEADER modal) and Employment each get a pen; the Covers chips and Requests do not.
function MediaSections() {
  const { mediaProfile, mediaProfileId, hashtags, mediaJobs, mayViewRequests, viewerContext, setOpenEditModal } =
    useContext(MediaProfileContext);

  const editable = !!viewerContext?.editable;
  const jobs = mediaJobs || [];
  const showAbout = editable || !!mediaProfile.bio || hashtags.length > 0;
  const showEmployment = editable || jobs.length > 0;

  return (
    <div className="container-fluid px-0">
      {showAbout && (
        <AboutSection
          id="media-section-about"
          eyebrowLabel="Covers:"
          eyebrowContent={
            hashtags.length > 0 ? (
              <HashtagsCollapsible hashtags={hashtags} hashtagUrlResolver={buildHashtagUrl} />
            ) : null
          }
          text={mediaProfile.bio}
          editable={editable}
          onEdit={() => setOpenEditModal("HEADER")}
          editButtonId="modal-button-ABOUT"
        />
      )}

      {showEmployment && (
        <EmploymentSection
          id="media-section-publications"
          rows={jobs.map(toEmploymentRow)}
          showDates
          editable={editable}
          onEdit={() => setOpenEditModal("PUBLICATIONS")}
          editButtonId="modal-button-PUBLICATIONS"
        />
      )}

      {mayViewRequests ? (
        <MediaRequestsSection mediaProfileId={mediaProfileId} />
      ) : (
        !!viewerContext.isAuthenticated &&
        viewerContext.role === "source" && (
          <MediaRequestsTeaser name={mediaProfile.name} upgradeUrl={viewerContext.upgradeUrl} />
        )
      )}
    </div>
  );
}

export default MediaSections;
